import * as Sentry from '@sentry/vue';

// DSN of the dedicated "kiosk-payments" Sentry project (greetmenu org).
// While empty, kiosk payment events fall back to the main app project.
export const KIOSK_SENTRY_DSN =
  'https://5a82d7281bb6572ab4a8b1b8b823c168@o4504293931024384.ingest.us.sentry.io/4512373914009600';

export const KIOSK_PAYMENT_TAG = { key: 'feature', value: 'kiosk_payment' };

// Used by makeMultiplexedTransport: events tagged as kiosk payment go only to
// the kiosk project; returning [] sends everything else to the main DSN.
export const kioskSentryMatcher = ({ getEvent }) => {
  if (!KIOSK_SENTRY_DSN) {
    return [];
  }
  const event = getEvent();
  if (event?.tags?.[KIOSK_PAYMENT_TAG.key] === KIOSK_PAYMENT_TAG.value) {
    return [KIOSK_SENTRY_DSN];
  }
  return [];
};

const USER_CANCEL_CODES = ['UC', 'TA'];
const DECLINE_CODES = ['BC', 'NB', 'CF'];

// Terminal response codes are documented in InPersonPaymentErrorBottomSheet.vue.
// Customer-driven outcomes must not look like outages in Sentry.
export const levelForTerminalCode = code => {
  const normalized = `${code ?? ''}`.toUpperCase();
  if (USER_CANCEL_CODES.includes(normalized)) {
    return 'info';
  }
  if (DECLINE_CODES.includes(normalized)) {
    return 'warning';
  }
  if (/^\d{3}$/.test(normalized)) {
    const numeric = Number(normalized);
    if ((numeric >= 100 && numeric <= 200) || numeric >= 900) {
      return 'warning';
    }
  }
  return 'error';
};

// Only the fields needed to follow a transaction; receipts are left out on
// purpose (long, and they carry masked card data).
export const summarizeTerminalPayload = data => {
  if (!data || typeof data !== 'object') {
    return { raw: `${data}`.slice(0, 200) };
  }
  return {
    operation: data.operation,
    CRN: data.CRN,
    result: data.result,
    responseCode: data.responseCode,
    responseText: data.responseText,
    amount: data.amount,
  };
};

// Breadcrumbs ride along with every kiosk payment event, so a stuck sale
// shows exactly which terminal messages were sent and received.
export const addTerminalBreadcrumb = (message, data = {}, level = 'info') => {
  try {
    Sentry.addBreadcrumb({
      category: 'terminal',
      message,
      data,
      level,
    });
  } catch (error) {
    // never let diagnostics break the payment flow
  }
};

export const captureKioskPaymentError = (
  name,
  { code = null, level = 'error', error = null, extra = {} } = {}
) => {
  try {
    Sentry.withScope(scope => {
      scope.setTag(KIOSK_PAYMENT_TAG.key, KIOSK_PAYMENT_TAG.value);
      scope.setTag('kiosk_error', name);
      if (code) {
        scope.setTag('response_code', `${code}`);
      }
      if (extra.restaurantId) {
        scope.setTag('restaurant_id', `${extra.restaurantId}`);
      }
      scope.setLevel(level);
      scope.setFingerprint(['kiosk-payment', name, `${code ?? ''}`]);
      scope.setExtras({ ...extra, code });

      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(
          code ? `KIOSK_PAYMENT: ${name} (${code})` : `KIOSK_PAYMENT: ${name}`
        );
      }
    });
  } catch (reportingError) {
    // reporting must never break the payment flow
    console.error('KIOSK_PAYMENT: failed to report to Sentry', reportingError);
  }
};
